'use client';

import { cn } from '@/app/lib/utils';
import { useMemo, useEffect, useRef } from 'react';
import React from 'react';
import { CustomScrollBarProps } from './scrollbar.types';
import { ScrollContent, ScrollThumb, ScrollTrack } from './ui';
import { useCustomScrollBar } from './useCustomScrollbar';
import { theme } from '@workspace/ui/theme';

export function CustomScrollBar({
  children,
  className,
  contentClassName,
  trackClassName,
  thumbClassName,
  thumbWidth,
  thumbHeight,
  thumbFixedWidth,
  thumbFixedHeight,
  maxHeight,
  maxWidth,
  autoHide = false,
  externalScrollRef,
  trackInsetTopPx = 0,
  trackInsetBottomPx = 0,
  orientation = 'vertical',
}: CustomScrollBarProps) {
  const {
    scrollRef,
    trackVRef,
    thumbVRef,
    trackHRef,
    thumbHRef,
    scrollAreaId,
    overflowV,
    overflowH,
    ariaNowV,
    ariaMaxV,
    ariaNowH,
    ariaMaxH,
    onScroll,
    startDrag,
    recalculate,
  } = useCustomScrollBar({
    thumbFixedWidth,
    thumbFixedHeight,
    trackInsetTopPx,
    trackInsetBottomPx,
    orientation,
  });

  // Track children count to detect changes (e.g., when Sanity adds new items)
  const childrenCountRef = useRef(React.Children.count(children));
  const scrollHeightRef = useRef<number>(0);

  const backgroundColor = theme === 'redRec' ? 'bg-light-gray' : 'bg-accent';

  useEffect(() => {
    const currentCount = React.Children.count(children);
    // Check if the number of children changed
    if (childrenCountRef.current !== currentCount) {
      childrenCountRef.current = currentCount;
      // Recalculate scrollbar after children change
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          recalculate();
        });
      });
    }
  }, [children, recalculate]);

  // Periodic check for scrollHeight changes as a fallback (for Sanity updates)
  const scrollWidthRef = useRef<number>(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      scrollHeightRef.current = el.scrollHeight;
      scrollWidthRef.current = el.scrollWidth;
    }

    const interval = setInterval(() => {
      const el = scrollRef.current;
      if (el) {
        const currentScrollHeight = el.scrollHeight;
        const currentScrollWidth = el.scrollWidth;
        // If scrollHeight/scrollWidth changed, recalculate
        if (
          scrollHeightRef.current !== currentScrollHeight ||
          ((orientation === 'horizontal' || orientation === 'both') && scrollWidthRef.current !== currentScrollWidth)
        ) {
          scrollHeightRef.current = currentScrollHeight;
          scrollWidthRef.current = currentScrollWidth;
          recalculate();
        }
      }
    }, 300); // Check every 300ms

    return () => clearInterval(interval);
  }, [recalculate, orientation, scrollRef]);

  const containerClasses = useMemo(() => cn('relative w-full h-full', className), [className]);

  const trackVClasses = useMemo(
    () => cn(thumbWidth || 'w-1', backgroundColor || 'rounded-[4px]', trackClassName),
    [thumbWidth, backgroundColor, trackClassName],
  );

  const thumbVClasses = useMemo(() => cn(thumbWidth || 'w-1', thumbClassName), [thumbWidth, thumbClassName]);

  const trackHClasses = useMemo(
    () => cn(thumbHeight || 'h-1', backgroundColor || 'rounded-[4px]', trackClassName),
    [thumbHeight, backgroundColor, trackClassName],
  );

  const thumbHClasses = useMemo(() => cn(thumbHeight || 'h-1', thumbClassName), [thumbHeight, thumbClassName]);

  return (
    <div className={containerClasses}>
      <ScrollContent
        scrollRef={scrollRef}
        externalScrollRef={externalScrollRef}
        scrollAreaId={scrollAreaId}
        className={contentClassName}
        maxHeight={maxHeight}
        maxWidth={maxWidth}
        onScroll={onScroll}
      >
        {children}
      </ScrollContent>

      {/* Vertical Scrollbar */}
      {(orientation === 'vertical' || orientation === 'both') && overflowV && (
        <ScrollTrack orientation="vertical" className={trackVClasses} trackRef={trackVRef} autoHide={autoHide}>
          <ScrollThumb
            orientation="vertical"
            className={thumbVClasses}
            scrollAreaId={scrollAreaId}
            ariaNow={ariaNowV}
            ariaMax={ariaMaxV}
            thumbRef={thumbVRef}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => startDrag(e, 'y')}
          />
        </ScrollTrack>
      )}

      {/* Horizontal Scrollbar */}
      {(orientation === 'horizontal' || orientation === 'both') && overflowH && (
        <ScrollTrack orientation="horizontal" className={trackHClasses} trackRef={trackHRef} autoHide={autoHide}>
          <ScrollThumb
            orientation="horizontal"
            className={thumbHClasses}
            scrollAreaId={scrollAreaId}
            ariaNow={ariaNowH}
            ariaMax={ariaMaxH}
            thumbRef={thumbHRef}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => startDrag(e, 'x')}
          />
        </ScrollTrack>
      )}
    </div>
  );
}
